import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Mail, AlertCircle } from 'lucide-react'
import { authAPI } from '../services/api'

function ForgotPasswordOtpPage() {
  const [digits, setDigits] = useState(['', '', '', '', '', ''])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [countdown, setCountdown] = useState(60)
  const [resending, setResending] = useState(false)
  const inputRefs = useRef([])
  const navigate = useNavigate()

  const resetEmail = sessionStorage.getItem('rithmo_reset_email') || ''
  const resetToken = sessionStorage.getItem('rithmo_reset_token') || ''

  // Redirect if no reset session
  useEffect(() => {
    if (!resetToken) {
      navigate('/forgot-password', { replace: true })
    }
  }, [resetToken, navigate])

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) return
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000)
    return () => clearTimeout(timer)
  }, [countdown])

  // Auto-focus first input
  useEffect(() => {
    inputRefs.current[0]?.focus()
  }, [])

  const handleSubmit = useCallback(async (otpDigits) => {
    const otp = otpDigits.join('')
    if (otp.length !== 6) return

    setLoading(true)
    setError('')

    try {
      await authAPI.verifyResetOtp(resetToken, otp)
      navigate('/forgot-password/reset', { replace: true })
    } catch (err) {
      setError(err.message || 'Invalid code. Please try again.')
      setDigits(['', '', '', '', '', ''])
      setTimeout(() => inputRefs.current[0]?.focus(), 50)
    } finally {
      setLoading(false)
    }
  }, [resetToken, navigate])

  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) return

    const newDigits = [...digits]
    newDigits[index] = value
    setDigits(newDigits)

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }

    if (value && newDigits.every((d) => d !== '')) {
      handleSubmit(newDigits)
    }
  }

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (!text) return
    const newDigits = [...digits]
    for (let i = 0; i < 6; i++) {
      newDigits[i] = text[i] || ''
    }
    setDigits(newDigits)
    const nextEmpty = newDigits.findIndex((d) => !d)
    inputRefs.current[nextEmpty >= 0 ? nextEmpty : 5]?.focus()
    if (newDigits.every((d) => d !== '')) {
      handleSubmit(newDigits)
    }
  }

  const handleResend = async () => {
    if (countdown > 0 || resending) return
    setResending(true)
    setError('')
    try {
      await authAPI.resendResetOtp(resetToken)
      setCountdown(60)
    } catch (err) {
      setError(err.message || 'Failed to resend code.')
    } finally {
      setResending(false)
    }
  }

  if (!resetToken) return null

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left Side - Visual */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-gradient-to-br from-primary-50 to-accent-50 p-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="max-w-lg text-center"
        >
          <div className="w-48 h-24 mx-auto mb-8 flex items-center justify-center">
            <img src="/rithmo-mark.png" alt="Rithmo logo" className="w-full h-full object-contain" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Check Your Inbox
          </h2>
          <p className="text-gray-600 text-lg">
            We've sent a verification code to your email address.
          </p>
        </motion.div>
      </div>

      {/* Right Side - OTP Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 mb-12">
            <div className="w-12 h-10 flex items-center justify-center">
              <img src="/rithmo-mark.png" alt="Rithmo logo" className="w-full h-full object-contain" />
            </div>
            <span className="text-xl font-bold text-gray-900">Rithmo</span>
          </Link>

          {/* Icon */}
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-50 to-accent-50 flex items-center justify-center mb-6">
            <Mail className="w-8 h-8 text-primary-600" />
          </div>

          {/* Header */}
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Enter verification code</h1>
          <p className="text-gray-500 mb-8">
            We sent a 6-digit code to{' '}
            <span className="font-medium text-gray-700">{resetEmail || 'your email'}</span>.
            Enter it below to continue.
          </p>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 mb-6"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </motion.div>
          )}

          {/* OTP Input Boxes */}
          <div className="flex justify-center gap-3 mb-8">
            {digits.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                pattern="[0-9]"
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                disabled={loading}
                className="w-12 h-14 text-center text-2xl font-bold rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-all disabled:opacity-50 bg-white"
              />
            ))}
          </div>

          {/* Loading indicator */}
          {loading && (
            <div className="flex justify-center mb-6">
              <div className="w-6 h-6 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
            </div>
          )}

          {/* Resend */}
          <p className="text-center text-gray-500">
            Didn't receive the code?{' '}
            {countdown > 0 ? (
              <span className="text-gray-400">Resend in {countdown}s</span>
            ) : (
              <button
                onClick={handleResend}
                disabled={resending}
                className="text-primary-600 hover:text-primary-700 font-medium transition-colors disabled:opacity-50"
              >
                {resending ? 'Sending...' : 'Resend Now'}
              </button>
            )}
          </p>
        </motion.div>
      </div>
    </div>
  )
}

export default ForgotPasswordOtpPage
